<template>
  <div class="fulfillment-question-main">
    <div v-if="isLoading" class="loading-container">
        <div v-if="this.ecertPortalUrl"/>
        <div class="waiting-text">
          <VueFeather type="loader" size="40" animation="spin" stroke="#4a4a4a"></VueFeather>
            <div class="waiting-text-content">
              Se abrirá una nueva ventana para realizar tu validación de identidad. Por favor, mantén esta pantalla abierta mientras realizas la validación. 
              Una vez que termines, regresa a esta ventana para finalizar este proceso.
            </div>
        </div>
      <div class="information-card" v-if="isLoading">
        <div v-if="this.ecertPortalUrl">
          <PdsCard class="fulfillment-card">
            <div class="card-title">Importante: No cierres esta ventana, es necesaria para completar tu validación. </div>
              <div class="card-text">
                    ¿No se abrió la ventana de validación? Haz clic en el siguiente enlace para continuar con tu proceso de verificación.
                <PdsLink
                  class="fulfillment-link"
                  text="Abrir validación de identidad"
                  :link="this.ecertPortalUrl"
                  v-bind:target="'_blank'"
                />
              </div>
          </PdsCard>
      </div>
    </div>
    </div>
    <div v-else-if="!isLoading">
      <div class="fulfillment-left">
        <div class="title-container">Portal de validación de Identidad</div>
          <a @click="$emit('goBack')" class="fulfillment-link" v-bind:style="{ color: '#0061A0' }">
            <vue-feather class="left-arrow-icon" type="arrow-left" size="24" stroke="#0061A0"></vue-feather> Volver
          </a>  
        <div class="fulfillment-title-container">
          <h1 class="fulfillment-title">Formulario datos personales</h1>
        </div>
        <span class="fulfillment-paragraph-step-2">
          Necesitamos que rellenes los siguientes datos para continuar con el proceso de validación:
        </span>

        <div v-if="error" class="fulfillment-message-error fulfillment-mb-20">
          <span class="fulfillment-round fulfillment-message-error-icon fulfillment-v-middle">!</span>
          <span class="fulfillment-v-middle">Los datos ingresados no coinciden</span>
        </div>

        <div class="fulfillment-form">
          <div class="fulfillment-mb-20">
            <div class="fulfillment-input-title">RUT <span class="req">*</span></div>
            <input type="text" v-model="clientRut" name="rut" placeholder="Ejemplo: 8455411-9" readonly required/>
          </div>

          <div class="fulfillment-mb-20">
            <div class="fulfillment-input-title">Nombre <span class="req">*</span></div>
            <input type="text" v-model="clientName" @paste="noPaste" name="clientName" required>
          </div>

          <div class="fulfillment-mb-20">
            <div class="fulfillment-input-title">Apellido Paterno <span class="req">*</span></div>
            <input type="text" v-model="clientLastName" @paste="noPaste" name="clientLastName" required/>
          </div>

          <div class="fulfillment-mb-20">
            <div class="fulfillment-input-title">Apellido Materno</div>
            <input type="text" v-model="clientMotherLastName" @paste="noPaste" name="clientMotherLastName"/>
          </div>

          <div class="fulfillment-mb-20">
            <div class="fulfillment-input-title">Correo <span class="req">*</span></div>
            <input type="text" v-model="clientEmail" @paste="noPaste" name="clientEmail"/>
          </div>
        </div>

        <div class="fulfillment-footer-section">
          <div class="fulfillment-left">
            <button
              v-if="!isLoading"
              v-bind:style="{
                backgroundColor: requiredFieldsFilled ? '#0091DA' : '#e6e6e6'
              }"
              v-bind:class="{
                'fulfillment-blue': requiredFieldsFilled,
                'fulfillment-disabled': !requiredFieldsFilled
              }"
              class="fulfillment-button"
              @click="startValidation"
            >
              Continuar
            </button>
            <button v-else class="fulfillment-button disabled">
              <svg
                class="fulfillment-spinner"
                height="40"
                width="40"
                :style="{ strokeDasharray: '132px', strokeDashoffset: '80px' }"
              >
                <circle cx="20" cy="20" r="10" stroke="#0091DA" stroke-width="4" fill="#e6e6e6" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Config from "@/utils/config";
import VueFeather from "vue-feather";
import { FFResultEvent } from "fulfillment-commons-js";
import PdsLink from "@vue-pds/link";
import PdsCard from "@vue-pds/card";

const AUTH_STEP = "ecertValidation"

export default {
  name: "EcertValidation",
  components: {
    VueFeather,
    PdsLink,
    PdsCard
  },
  data() {
    return {
      isLoading: false,
      error: false,
      processFinished: false,
      config: undefined,
      company: "principal",
      clientRut: this.rut,
      clientName: this.name,
      clientLastName: this.lastName,
      clientMotherLastName: this.motherLastName,
      clientEmail: this.email,
      portalIdentifier: this.portalId,
      environment: Config.buildEnvironment(),
      ecertPortalUrl: "",
      pollingIntervalInMillis: process.env.VUE_APP_ECERT_POLLING_INTERVAL,
    }
  },
  created() {
    this.config = new Config(this.environment);
    this.config.printInfo();
  },
  props: {
    channelKey: { type: String },
    dummyMode: { type: Boolean, default: false },
    ecertService: { type: Object },
    email: { type: String, required: true },
    lastName: { type: String, required: true },
    motherLastName: { type: String, required: false },
    name: { type: String, required: true },
    portalId: { type: String, required: false },
    rut: { type: String, required: true },
    traceClient: {type: Object, required: true},
  },
  methods: {
    noPaste: e => e.preventDefault(),
    nextStepSuccess(response) {
      this.isLoading = false;
      if (response) {
        this.successResponse(response);
      }
    },
    nextStepError(error) {
      this.isLoading = false;
      if (error) {
        this.failureResponse(error)
      }
    },
    pollStatus(transactionId, startTime) {
      const timePassed = Date.now() - startTime;
      if (timePassed < process.env.VUE_APP_ECERT_POLLING_MAX_TIME) {
        setTimeout(() => {
          this.ecertService
          .getStatus(transactionId)
          .then((response) => {
            switch (response.status) {
              case ("USED"):
              this.nextStepSuccess(response);
                break;
              case "PENDING":
                this.pollStatus(transactionId, startTime);
                break;
              case "EXPIRED":
                this.nextStepError({
                    reason: response.error.code,
                    message: response.error.detail,
                    source: response.error.source,
                  });
                break;
              case undefined:
                this.nextStepError({
                  reason: response.reason,
                  message: response.message,
                  source: response.error.source,
                });
                break;
            }
          })
          .catch(() => {
            this.nextStepError({
              reason: "COMMUNICATION_ERROR",
              message: `Failed to obtain E-Cert state info`,
              source: "auth-widget",
            });
          });
        }, this.pollingIntervalInMillis)
      }
      else {
        this.nextStepError({
          reason: "POLLING_EXPIRED",
          message: `Limit time for polling reached`,
          source: "auth-widget",
        });
      }
    },
    startValidation() {
      this.error = false;
      this.traceClient.info(`${AUTH_STEP}.startValidation`, "Creating transaction", `{clientId: ${this.rut}}`);

      if (this.rut) {
        this.isLoading = true;
        this.ecertService.getValidation(
          {
            rut: this.clientRut,
            clientName: this.clientName,
            clientLastName: this.clientLastName,
            clientMotherLastName: this.clientMotherLastName,
            clientMail: this.clientEmail,
            company: this.company,
            portalId: this.portalId === "" ? undefined : this.portalId
          },
          this.channelKey
        )
        .then(response => {
          if (this.dummyMode) {
            const transactionId = "123123"
            this.pollStatus(transactionId, Date.now())
          }
          else{
            this.ecertPortalUrl = response.urlEcert;
            this.traceClient.info(
              `${AUTH_STEP}.startValidation`,
              "Transaction created",
              `{rut: ${this.rut}, transactionId: ${response.transactionId}}`
            );
            window.open(this.ecertPortalUrl, "_blank");
            this.pollStatus(response.transactionId, Date.now())
          }
        })
        .catch(error => {
          console.error(`Failure while initializing validation: ${JSON.stringify(error)}`);
          this.isLoading = false;
          this.failureResponse(error);
          this.traceClient.error(
            `${AUTH_STEP}.startValidation`,
            "Couldn't create transaction",
            `{data: ${JSON.stringify(error)}}`
          );
          this.error = true;
          window.location.href = "#";
        })
      }
    },
    successResponse(response) {
      this.$emit("result", FFResultEvent.success(response, this.traceId));   
    },
    failureResponse(error) {
      this.$emit("result", FFResultEvent.failure(error, this.traceId));
    },
  },
  computed: {
    requiredFieldsFilled() {
      return this.rut !== "" && this.clientName !== "" && this.clientLastName !== "" && this.clientEmail !== ""
    }
  },
};
</script>
<style scoped>
.fulfillment-question-main {
  height: inherit;
}

.loading-container {
  height: inherit;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.container-message{
  text-align: center;
}
.waiting-text {
  min-height: 400px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.waiting-text-content {
  margin: 1rem 0;
  line-height: 1.5;
}

.action-link {
  text-align: start;
  line-height: 1.5;
  padding-bottom: 5rem;
}

.fulfillment-title-container {
  display: flex;
  align-items: center;
}
.fulfillment-card {
  padding-top: 24px;
  padding-bottom: 24px;
  padding-left: 64px;
  padding-right: 64px;
  background-color: #e3f5fd;
  margin-bottom: 5%;
}
.card-title {
  color: #000000;
  font-family: FS Elliot Pro, sans-serif;
  font-size: 18px;
  font-weight: bold;
  letter-spacing: 0;
  line-height: 24px;
  text-align: left;
}
.card-text {
  color: #000000;
  font-family: FS Elliot Pro, sans-serif;
  font-size: 16px;
  letter-spacing: 0;
  line-height: 24px;
  text-align: left;
}


.fulfillment-back-container {
  display: inline-flex;
  width: 20px;
  height: 32px;
  cursor: pointer;
}

.fulfillment-back-container svg {
  transform: scale(0.7);
  width: 32px;
}

.fulfillment-title {
  color: #000000;
  font-family: FS Elliot Pro, sans-serif;
  font-size: 24px;
  letter-spacing: 0;
  line-height: 35px;
  text-align: left;
}

.fulfillment-paragraph {
  font-size: 18px;
  margin-top: 0px;
}

.fulfillment-paragraph-step-2 {
  color: #464646;
  font-family: FS Elliot Pro, sans-serif;
  font-size: 18px;
  letter-spacing: 0;
  line-height: 24px;
  text-align: start;
  width: 100%;

}

.fulfillment-v-middle {
  vertical-align: middle;
}
.fulfillment-mb-20 {
  margin-top: 20px;
}
.fulfillment-pl-15 {
  padding-left: 15px;
}
.fulfillment-pr-15 {
  padding-right: 15px;
}
.fulfillment-w-100 {
  width: 100%;
}

.fulfillment-modal-header {
  font-size: 20px;
  margin-bottom: 27px;
  padding-right: 50px;
  margin-top: 0px;
}

input[type="text"] {
  border-radius: 2px;
  line-height: 1;
  border: 1px solid #dedede;
  width: 100%;
  padding: 12px;
  box-sizing: border-box;
  color: #4d4e53;
  font-size: 16px;
}

.fulfillment-spinner {
  animation-duration: 1s;
  animation-name: spinner;
  animation-iteration-count: infinite;
}


.fulfillment-disabled {
  cursor: default;
  pointer-events: none;
  outline: none;
}

.fulfillment-list {
  padding-left: 20px;
  font-size: 18px;
  line-height: 1.5;
}

.fulfillment-list li {
  padding: 0px 10px;
}

.fulfillment-align {
  display: flex;
  align-items: center;
}

.fulfillment-footer-section {
  display: table;
  width: 100%;
  margin-top: 27px;
}

.fulfillment-rut-pictures-container {
  margin: 10px auto;
}

.fulfillment-form {
  max-width: 345px;
}
.fulfillment-left {
  text-align: start;
  max-width: 1200px;
  margin: auto;
}
.fulfillment-href {
  text-decoration: underline;
  cursor: pointer;
}

.fulfillment-input-title {
  color: #4a4a4a;
  font-family: FS Elliot Pro, sans-serif;
  font-size: 14px;
  letter-spacing: 0;
  line-height: 20px;
  text-align: left;
}

.fulfillment-link {
  color: #0091da;
  font-weight: bold;
  text-decoration: underline dashed;
}

.fulfillment-form .fulfillment-link {
  display: block;
}

.fulfillment-round {
  border-radius: 100%;
}

.fulfillment-button {
  min-height: 44px;
  min-width: 149px;
  border-radius: 22px;
  background-color: #e6e6e6;
  border: none;
  font-weight: bold;
  color: #757575;
  font-size: 16px;
  cursor: pointer;
  margin-bottom: 5%;
}

.fulfillment-message-error {
  color: #b30000;
  border: 1px solid;
  border-radius: 5px;
  vertical-align: middle;
}

.fulfillment-message-error-icon {
  background-color: #b30000;
  color: white;
  width: 24px;
  height: 24px;
  text-align: center;
  display: inline-block;
  line-height: 1;
  font-size: 24px;
  font-weight: bold;
  margin: 15px;
}

.fulfillment-rut-detail {
  display: block;
  margin-bottom: 10px;
  font-size: 15px;
}

.fulfillment-blue {
  background-color: #0091da;
  color: white;
  outline: none;
}
input,
button {
  outline: none;
  font-family: "FS Elliot Pro", Helvetica, Arial, sans-serif;
}
.fulfillment-blue-text {
  color: #0091da;
}

.req {
  color: red;
}

.title-container {
  width: 100%;
  font-size: 50px;
  line-height: 66px;
  font-weight: bold;
  align-items: center;
  text-align: center;
  color: #000000;
}

@media screen and (max-width: 674px) {

  .fulfillment-rut-pictures-container img {
    max-width: 290px;
  }
}

@media screen and (max-width: 480px) {
  .fulfillment-form {
    max-width: 100%;
  }
  .fulfillment-form .fulfillment-link {
    text-align: right;
  }
}
</style>

